import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, TextInput as RNTextInput } from 'react-native';
import {
    Menu,
    Button,
    Text,
    Divider,
    ActivityIndicator,
    useTheme,
    Surface,
    Icon,
    Portal,
    Modal,
    TextInput,
} from 'react-native-paper';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCompanyContext } from '../context/CompanyContext';
import type { OwnedCompanySummary } from '../api/apiService';
import { HelpTooltip } from './HelpTooltip';
import { InfoBox } from './InfoBox';
import helpContent from '../locales/pt/help.json';
import { addCompanyAsOwner } from '../api/apiService';
import { OperatorLicenseInput } from './OperatorLicenseInput';

const MULTI_FLEET_TIP_DISMISSED_KEY = '@alldrivers:multi_fleet_tip_dismissed';

interface FleetSwitcherProps {
    onCompanyChange?: (companyId: number) => void;
    isOwner?: boolean;
    companiesOverride?: OwnedCompanySummary[];
    selectedCompanyOverride?: OwnedCompanySummary | null;
    setSelectedCompanyOverride?: (companyId: number) => Promise<void> | void;
}

export const FleetSwitcher: React.FC<FleetSwitcherProps> = ({ onCompanyChange, isOwner = false, companiesOverride, selectedCompanyOverride, setSelectedCompanyOverride }) => {
    const theme = useTheme();
    const { companies: contextCompanies, selectedCompany: contextSelectedCompany, setSelectedCompany: contextSetSelectedCompany, isLoading, refreshCompanies, error } = useCompanyContext();
    const companies = companiesOverride ?? contextCompanies;
    const selectedCompany = selectedCompanyOverride ?? contextSelectedCompany;
    const setSelectedCompany = setSelectedCompanyOverride ?? contextSetSelectedCompany;
    const [menuVisible, setMenuVisible] = useState(false);
    const [addCompanyModalVisible, setAddCompanyModalVisible] = useState(false);
    const [licenseTypeMenuVisible, setLicenseTypeMenuVisible] = useState(false);
    const [newCompanyData, setNewCompanyData] = useState({
        name: '',
        nipc: '',
        certidao_permanente: '',
        tipo_licenca_operacao: '',
        numero_licenca: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [multiFleetTipDismissed, setMultiFleetTipDismissed] = useState(true); // Default to true to avoid flash

    // Load dismissed state from AsyncStorage on mount
    useEffect(() => {
        const loadDismissedState = async () => {
            try {
                const dismissed = await AsyncStorage.getItem(MULTI_FLEET_TIP_DISMISSED_KEY);
                setMultiFleetTipDismissed(dismissed === 'true');
            } catch (error) {
                console.error('Error loading multi-fleet tip dismissed state:', error);
                setMultiFleetTipDismissed(false);
            }
        };
        loadDismissedState();
    }, []);

    // Mask function for certidao permanente (12 digits formatted as XXXX-XXXX-XXXX)
    const applyMaskCertidao = (value: string): string =>
        value
            .replace(/\D/g, '')
            .replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')
            .substring(0, 14);


    // License type options matching backend choices
    const licenseTypeOptions = [
        { label: 'Selecionar tipo...', value: '' },
        { label: 'Operador TVDE', value: 'Operador TVDE' },
        { label: 'Taxi', value: 'Taxi' },
        { label: 'Veículos ligeiros de mercadorias', value: 'Veículos ligeiros de mercadorias' },
        { label: 'Transporte Rodoviário (Sem Tacógrafo)', value: 'Transporte Rodoviário (Sem Tacógrafo)' },
        { label: 'Outro', value: 'Outro' },
    ];

    const handleSelectCompany = async (companyId: number) => {
        try {
            await setSelectedCompany(companyId);
            setMenuVisible(false);
            if (onCompanyChange) {
                onCompanyChange(companyId);
            }
        } catch (error) {
            console.error('Error switching company:', error);
        }
    };

    const handleDismissMultiFleetTip = async () => {
        try {
            await AsyncStorage.setItem(MULTI_FLEET_TIP_DISMISSED_KEY, 'true');
            setMultiFleetTipDismissed(true);
        } catch (error) {
            console.error('Error saving multi-fleet tip dismissed state:', error);
        }
    };

    const handleAddCompany = async () => {
        if (!newCompanyData.name || !newCompanyData.nipc) {
            setFormError('Nome e NIPC são obrigatórios');
            return;
        }

        // Validate certidao_permanente if provided (must be exactly 12 digits)
        if (newCompanyData.certidao_permanente) {
            const digitsOnly = newCompanyData.certidao_permanente.replace(/\D/g, '');
            if (digitsOnly.length !== 12) {
                setFormError('A certidão permanente deve conter exatamente 12 dígitos');
                return;
            }
        }

        // Validate license number format if provided (NNNNNNNNNN/AAAA)
        if (newCompanyData.numero_licenca) {
            const licenseRegex = /^\d{1,10}\/\d{4}$/;
            if (!licenseRegex.test(newCompanyData.numero_licenca)) {
                setFormError('O número da licença deve estar no formato NNNNNNNNNN/AAAA (ex: 1234567890/2024)');
                return;
            }
            // Validate year range (1900 to current_year + 5)
            const parts = newCompanyData.numero_licenca.split('/');
            const yearStr = parts[1];
            const year = parseInt(yearStr, 10);
            const currentYear = new Date().getFullYear();
            if (isNaN(year) || year < 1900 || year > currentYear + 5) {
                setFormError(`Ano inválido: ${yearStr}. Deve estar entre 1900 e ${currentYear + 5}.`);
                return;
            }
        }

        setIsSubmitting(true);
        setFormError(null);

        try {
            const response = await addCompanyAsOwner(
                newCompanyData.name,
                newCompanyData.nipc,
                newCompanyData.certidao_permanente || undefined,
                newCompanyData.tipo_licenca_operacao || undefined,
                newCompanyData.numero_licenca || undefined
            );

            if (response.ok || response.status === 200) {
                // Success!
                setAddCompanyModalVisible(false);
                setNewCompanyData({ name: '', nipc: '', certidao_permanente: '', tipo_licenca_operacao: '', numero_licenca: '' });
                await refreshCompanies();
                
                // Select the new company
                if (response.data?.id) {
                    await setSelectedCompany(response.data.id);
                    if (onCompanyChange) {
                        onCompanyChange(response.data.id);
                    }
                }
            } else {
                setFormError(response.data?.detail || response.data?.message || 'Erro ao criar empresa');
            }
        } catch (err) {
            setFormError('Erro ao criar empresa. Tente novamente.');
            console.error('Error adding company:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Show loading state if we're loading AND we don't have any companies yet
    // This prevents showing loading spinner when companies are already loaded but selectedCompany is still null
    // Also show loading on first render before companies have been fetched
    if (isLoading && (!companies || companies.length === 0)) {
        return (
            <Surface style={[styles.container, { backgroundColor: theme.colors.surface }]}>
                <ActivityIndicator size="small" testID="activity-indicator" />
            </Surface>
        );
    }

    // Don't render if user has no companies AND is not an owner
    // Owners should always see the switcher (even if empty) to allow creating companies
    if ((!companies || companies.length === 0) && !isOwner) {
        return null;
    }
    
    // CRITICAL: Never render for non-owners (drivers) - they should never see company selection
    if (!isOwner) {
        return null;
    }

    const hasMultipleCompanies = companies && companies.length > 1;
    const singleCompany = companies && companies.length === 1 ? companies[0] : null;
    // Use selectedCompany if available, otherwise fall back to singleCompany, or first company if multiple
    // This ensures we always show a company if one exists
    const displayCompany = selectedCompany || singleCompany || (companies && companies.length > 0 ? companies[0] : null);

    return (
        <View style={styles.container}>
            {/* Show error if companies failed to load */}
            {error && (
                <InfoBox
                    variant="warning"
                    title="Erro ao carregar empresas"
                    message={error}
                />
            )}
            {/* Dismissible info box for single company */}
            {!hasMultipleCompanies && !multiFleetTipDismissed && !error && (
                <InfoBox
                    variant="tip"
                    title="Multi-Frota Disponível"
                    message="Pode adicionar mais empresas para gerir múltiplas frotas independentes."
                    dismissible
                    onDismiss={handleDismissMultiFleetTip}
                />
            )}

            <View style={styles.headerRow}>
                <View style={styles.flexOne}>
                    <Menu
                        visible={menuVisible}
                        onDismiss={() => setMenuVisible(false)}
                        anchor={
                            <Button
                                mode="outlined"
                                onPress={() => {
                                    if (hasMultipleCompanies) {
                                        setMenuVisible(true);
                                    } else if (companies && companies.length === 0) {
                                        setAddCompanyModalVisible(true);
                                    } else {
                                        // Single company - open menu to show company and add option
                                        setMenuVisible(true);
                                    }
                                }}
                                onLongPress={() => refreshCompanies()} // Long press to refresh
                                style={[styles.button, { borderColor: theme.colors.primary }]}
                                contentStyle={styles.buttonContent}
                                labelStyle={styles.buttonLabel}
                            >
                                <View style={styles.buttonInnerContent}>
                                    <View style={styles.leftIcon}>
                                        <Icon source="office-building" size={20} color={theme.colors.primary} />
                                    </View>
                                    <View style={styles.selectedCompanyInfo}>
                                        <Text variant="titleSmall" style={styles.companyName} numberOfLines={1} ellipsizeMode="tail">
                                            {displayCompany?.name || 
                                             (companies && companies.length === 0 ? 'Adicionar Empresa' : 
                                              'Selecionar Empresa')}
                                        </Text>
                                        {displayCompany && (
                                            <Text variant="bodySmall" style={[styles.companyNipc, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
                                                NIPC: {displayCompany.nipc}
                                            </Text>
                                        )}
                                        {!displayCompany && companies && companies.length === 0 && (
                                            <Text variant="bodySmall" style={[styles.companyNipc, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
                                                Clique para criar
                                            </Text>
                                        )}
                                    </View>
                                    <View style={styles.rightIcon}>
                                        <Icon source="chevron-down" size={20} color={theme.colors.primary} />
                                    </View>
                                </View>
                            </Button>
                        }
                anchorPosition="bottom"
                contentStyle={[styles.menuContent, { backgroundColor: theme.colors.surface }]}
            >
                <ScrollView style={styles.menuScrollView}>
                    <Text variant="labelMedium" style={[styles.menuTitle, { color: theme.colors.onSurfaceVariant }]}>
                        Minhas Empresas
                    </Text>
                    <Divider style={styles.divider} />

                    {companies && companies.map((company) => (
                        <Menu.Item
                            key={company.id}
                            onPress={() => handleSelectCompany(company.id)}
                            title={company.name}
                            titleStyle={[
                                styles.menuItemTitle,
                                company.id === selectedCompany?.id && {
                                    color: theme.colors.primary,
                                    fontWeight: 'bold'
                                }
                            ]}
                            leadingIcon={
                                company.id === selectedCompany?.id
                                    ? "check-circle"
                                    : "office-building-outline"
                            }
                            style={[
                                styles.menuItem,
                                company.id === selectedCompany?.id && {
                                    backgroundColor: theme.colors.primaryContainer
                                }
                            ]}
                        />
                    ))}

                    {(!companies || companies.length === 0) && (
                        <>
                            <View style={styles.emptyState}>
                                <Icon source="office-building-off" size={48} color={theme.colors.onSurfaceDisabled} />
                                <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceDisabled, marginTop: 8 }}>
                                    Nenhuma empresa encontrada
                                </Text>
                            </View>
                            <Divider style={styles.divider} />
                            <Menu.Item
                                onPress={() => {
                                    setMenuVisible(false);
                                    setAddCompanyModalVisible(true);
                                }}
                                title="Adicionar Primeira Empresa"
                                titleStyle={{ color: theme.colors.primary, fontWeight: 'bold' }}
                                leadingIcon="plus-circle"
                                style={styles.addCompanyButton}
                            />
                        </>
                    )}

                    {/* Add Company Button - Always show for owners or when user has at least one company */}
                    {(hasMultipleCompanies || (companies && companies.length > 0)) && (
                        <>
                            <Divider style={styles.divider} />
                            <Menu.Item
                                onPress={() => {
                                    setMenuVisible(false);
                                    setAddCompanyModalVisible(true);
                                }}
                                title="Adicionar Nova Empresa"
                                titleStyle={{ color: theme.colors.primary, fontWeight: 'bold' }}
                                leadingIcon="plus-circle"
                                style={styles.addCompanyButton}
                            />
                        </>
                    )}
                </ScrollView>
            </Menu>
                </View>
                <HelpTooltip
                    helpKey="fleet_switcher"
                    content={helpContent.fleet_switcher}
                />
            </View>

            {/* Add Company Modal */}
            <Portal>
                <Modal
                    visible={addCompanyModalVisible}
                    onDismiss={() => {
                        setAddCompanyModalVisible(false);
                        setNewCompanyData({ name: '', nipc: '', certidao_permanente: '', tipo_licenca_operacao: '', numero_licenca: '' });
                        setFormError(null);
                    }}
                    contentContainerStyle={[
                        styles.modalContent,
                        { backgroundColor: theme.colors.surface }
                    ]}
                >
                    <Surface style={styles.modalSurface} elevation={4}>
                        <View style={styles.modalHeader}>
                            <Icon source="office-building-plus" size={24} color={theme.colors.primary} />
                            <Text variant="titleLarge" style={styles.modalTitle}>
                                Adicionar Nova Empresa
                            </Text>
                        </View>

                        <View style={styles.modalBody}>
                            <Text variant="bodyMedium" style={styles.modalDescription}>
                                Crie uma nova empresa para gerir múltiplas frotas de forma independente. 
                                Os campos marcados com * são obrigatórios.
                            </Text>

                            {formError && (
                                <Surface style={[styles.errorBox, { backgroundColor: theme.colors.errorContainer }]}>
                                    <Text variant="bodySmall" style={{ color: theme.colors.error }}>
                                        {formError}
                                    </Text>
                                </Surface>
                            )}

                            <TextInput
                                label="Nome da Empresa *"
                                value={newCompanyData.name}
                                onChangeText={(text) => setNewCompanyData({ ...newCompanyData, name: text })}
                                mode="outlined"
                                style={styles.input}
                                disabled={isSubmitting}
                            />

                            <TextInput
                                label="NIPC *"
                                value={newCompanyData.nipc}
                                onChangeText={(text) => setNewCompanyData({ ...newCompanyData, nipc: text })}
                                mode="outlined"
                                keyboardType="numeric"
                                maxLength={9}
                                style={styles.input}
                                disabled={isSubmitting}
                            />

                            <TextInput
                                label="Certidão Permanente"
                                value={newCompanyData.certidao_permanente}
                                onChangeText={(text) => setNewCompanyData({ 
                                    ...newCompanyData, 
                                    certidao_permanente: applyMaskCertidao(text)
                                })}
                                mode="outlined"
                                keyboardType="numeric"
                                maxLength={14}
                                placeholder="XXXX-XXXX-XXXX"
                                style={styles.input}
                                disabled={isSubmitting}
                            />

                            <Menu
                                visible={licenseTypeMenuVisible}
                                onDismiss={() => setLicenseTypeMenuVisible(false)}
                                anchor={
                                    <TextInput
                                        label="Tipo de Licença de Operação"
                                        value={newCompanyData.tipo_licenca_operacao}
                                        mode="outlined"
                                        style={styles.input}
                                        disabled={isSubmitting}
                                        editable={false}
                                        right={
                                            <TextInput.Icon
                                                icon="chevron-down"
                                                onPress={() => !isSubmitting && setLicenseTypeMenuVisible(true)}
                                            />
                                        }
                                        onPressIn={() => !isSubmitting && setLicenseTypeMenuVisible(true)}
                                        placeholder="Selecionar tipo..."
                                    />
                                }
                                contentStyle={{ marginTop: 8, maxWidth: 400 }}
                            >
                                <ScrollView style={{ maxHeight: 300 }}>
                                    {licenseTypeOptions.map((option) => (
                                        <Menu.Item
                                            key={option.value}
                                            onPress={() => {
                                                setNewCompanyData({ ...newCompanyData, tipo_licenca_operacao: option.value });
                                                setLicenseTypeMenuVisible(false);
                                            }}
                                            title={option.label}
                                            style={
                                                newCompanyData.tipo_licenca_operacao === option.value
                                                    ? { backgroundColor: theme.colors.primaryContainer }
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </ScrollView>
                            </Menu>

                            <OperatorLicenseInput
                                label="Número da Licença"
                                value={newCompanyData.numero_licenca}
                                onChangeText={(text) => setNewCompanyData({ 
                                    ...newCompanyData, 
                                    numero_licenca: text
                                })}
                                style={styles.input}
                                error={false}
                            />
                        </View>

                        <View style={styles.modalFooter}>
                            <Button
                                mode="outlined"
                                onPress={() => {
                                    setAddCompanyModalVisible(false);
                                    setNewCompanyData({ name: '', nipc: '', certidao_permanente: '', tipo_licenca_operacao: '', numero_licenca: '' });
                                    setFormError(null);
                                }}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </Button>
                            <Button
                                mode="contained"
                                onPress={handleAddCompany}
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                Criar Empresa
                            </Button>
                        </View>
                    </Surface>
                </Modal>
            </Portal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        paddingVertical: 12,
        paddingHorizontal: 16,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    flexOne: {
        flex: 1,
    },
    button: {
        borderWidth: 1.5,
        borderRadius: 12,
        minHeight: 56,
    },
    buttonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    buttonLabel: {
        marginLeft: 0,
        marginRight: 0,
        flex: 1,
    },
    buttonInnerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        width: '100%',
    },
    leftIcon: {
        marginRight: 12,
    },
    rightIcon: {
        marginLeft: 8,
    },
    selectedCompanyInfo: {
        flex: 1,
        alignItems: 'flex-start',
        paddingRight: 8,
        minWidth: 0, // Allow text to shrink
    },
    companyName: {
        fontWeight: '600',
        lineHeight: 20,
    },
    companyNipc: {
        fontSize: 12,
        marginTop: 2,
        lineHeight: 16,
    },
    menuContent: {
        maxHeight: 400,
        minWidth: 300,
        borderRadius: 16,
        marginTop: 8,
        paddingVertical: 4,
    },
    menuScrollView: {
        maxHeight: 360,
    },
    menuTitle: {
        paddingHorizontal: 16,
        paddingVertical: 14,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    divider: {
        marginVertical: 4,
    },
    menuItem: {
        minHeight: 56,
        paddingVertical: 10,
        paddingHorizontal: 4,
    },
    menuItemTitle: {
        fontSize: 15,
        lineHeight: 20,
    },
    companyDetails: {
        marginTop: 4,
        paddingLeft: 40,
    },
    detailsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 2,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
    },
    addCompanyButton: {
        minHeight: 48,
    },
    modalContent: {
        marginHorizontal: 20,
        maxWidth: 500,
        alignSelf: 'center',
        borderRadius: 12,
    },
    modalSurface: {
        borderRadius: 12,
        padding: 24,
    },
    modalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
        gap: 12,
    },
    modalTitle: {
        fontWeight: '600',
    },
    modalBody: {
        marginBottom: 24,
    },
    modalDescription: {
        marginBottom: 16,
        lineHeight: 20,
    },
    input: {
        marginBottom: 12,
    },
    errorBox: {
        padding: 12,
        borderRadius: 8,
        marginBottom: 16,
    },
    modalFooter: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
    },
});

export default FleetSwitcher;



